/**
 * FeatureSection — Web Component
 *
 * Handles viewport-driven video playback only.
 * Video play/pause is triggered by the IntersectionObserver
 * inline script in feature-section.liquid, which dispatches
 * the custom event 'fs:viewport-change'.
 */

class FeatureSection extends HTMLElement {
  connectedCallback() {
    this._onViewportChange = this._onViewportChange.bind(this);
    this.addEventListener('fs:viewport-change', this._onViewportChange);

    this._checkEmpty();

    // Play immediately if the section is already visible at mount time
    if (this.getAttribute('data-in-viewport') === 'true') {
      this._playVideo();
    }

    this._initMuteButton();
  }

  _checkEmpty() {
    const heading = this.querySelector('.feature-section__heading');
    const hasHeading = heading && heading.textContent.trim() !== '';
    const hasMedia = !!(
      this.querySelector('.feature-section__media-panel img') ||
      this.querySelector('.feature-section__media-panel video')
    );
    const hasListItems = this.querySelectorAll('.feature-section__list-item').length > 0;
    if (!hasHeading && !hasMedia && !hasListItems) {
      this.classList.add('feature-section--resolved-empty');
    } else {
      this.classList.remove('feature-section--resolved-empty');
    }
  }

  disconnectedCallback() {
    this.removeEventListener('fs:viewport-change', this._onViewportChange);
  }

  _onViewportChange(event) {
    if (event.detail.visible) {
      this._playVideo();
    } else {
      this._pauseVideo();
    }
  }

  _playVideo() {
    const video = this.querySelector('video');
    if (!video) return;
    const promise = video.play();
    if (promise !== undefined) {
      promise.catch(() => {
        // Autoplay blocked — silently ignore (poster will remain visible)
      });
    }
  }

  _pauseVideo() {
    const video = this.querySelector('video');
    if (video) video.pause();
  }

  _initMuteButton() {
    const btn = this.querySelector('.feature-section__mute-btn');
    if (!btn) return;
    // Remove any previously-bound handler before attaching (safe on re-init in editor)
    if (this._onMuteBtnClick) btn.removeEventListener('click', this._onMuteBtnClick);
    this._onMuteBtnClick = this._onMuteBtnClick.bind(this);
    btn.addEventListener('click', this._onMuteBtnClick);
  }

  _onMuteBtnClick() {
    const btn = this.querySelector('.feature-section__mute-btn');
    const video = this.querySelector('video');
    if (!btn || !video) return;
    const nowMuted = !video.muted;
    video.muted = nowMuted;
    btn.setAttribute('data-muted', nowMuted ? 'true' : 'false');
    btn.setAttribute(
      'aria-label',
      nowMuted
        ? btn.getAttribute('data-label-muted') || 'Unmute video'
        : btn.getAttribute('data-label-unmuted') || 'Mute video'
    );
    if (!nowMuted && video.paused) video.play().catch(() => {});
  }
}

customElements.define('feature-section', FeatureSection);

// --------------------------------------------------
// Shopify theme editor hooks
// --------------------------------------------------

if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const component = event.target.querySelector('feature-section');
    if (component) {
      // Re-run connectedCallback logic after section reload in the editor
      component.removeEventListener('fs:viewport-change', component._onViewportChange);
      component.connectedCallback();
    }
  });
}
